import { FC, JSX, useEffect, useState } from "react";

import { Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { QESAlert, QESButton, QESCheckbox, QESSelect, QESTextArea, QESTextInput } from "@app/shared/qes-standards";
import { RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import MemberSetService from "./memberSetService";

const service = new MemberSetService();

type MemberSetCreateFilteredProps = RouteComponentProps<unknown, unknown, { sourceMemberSetId: number }>;

const MemberSetCreateFiltered: FC<React.PropsWithChildren<MemberSetCreateFilteredProps>> = (props: MemberSetCreateFilteredProps) => {
  const [error, setError] = useState("");
  const [sourceOptions, setSourceOptions] = useState<JSX.Element[]>([]);
  const [source, setSource] = useState<number>(props.location.state ? (props.location.state).sourceMemberSetId : null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);

  useEffect(() => {
    // hit api to get list of source member sets
    //filter out any system resources and filtered member populations, these can not be used to create filtered populations
    service.get(true, true, true, false, true).then((response) => {
      // convert each to an option tag and deliver to setSourceOptions

      const options = response.map((r) => <option key={r.memberSetId} value={r.memberSetId}>{r.resource.name}</option>);
      setSourceOptions(options);
    }, () => {
      setError("We're having trouble retrieving your existing member sets. Please try again later.");
    });
  }, []);

  const handleSubmit = () => {
    if (!(source && name.length > 0 /*&& description.length > 0*/)) {
      return false;
    }
    // hit submit endpoint
    service.createFilteredMemberSet(source, name, description, isPrivate, undefined).then((response) => {
      toast(`Your filtered member set, ${name}, is being created.`, {
        containerId: "z-index-1030",
        onClose: () => props.history.replace(`/member-populations/${response.memberSetId}/detail`),
        type: "success",
      });
    }, () => {
      setError("We are unable to create a new member set at this time. Please try again later.");
    });
  };

  return (
    <Page documentTitle="Filtered Member Population Creation">
      <PageInner>
        <PageHeader title="Filtered Member Population Creation" />
        <PageSection>
          <h2 className="lead mt-3">Add a new Filtered Member Population</h2>
          <div className="col-12 col-md-6 mt-3">
            {error.length > 0 && <QESAlert classNames="mb-3" type="danger" persist={true}>{error}</QESAlert>}
            <QESSelect
              label="Source Member Population"
              onChange={(e) => setSource(parseInt((e.target as HTMLSelectElement).value, 10))}
              required={true}
              testId="create-filtered-member-set__source"
              value={source || ""}
            >
              {sourceOptions}
            </QESSelect>

            <QESTextInput
              label="Name"
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
              required={true}
              testId="create-filtered-member-set__name"
              value={name}
            />

            <QESTextArea label="Description" testId="created-filtered-member-set__description" value={description} onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />

            <QESCheckbox
              containerClassName="form-check"
              label="This is a Private Member Population (can be administered only by you)"
              id="is-private"
              className="form-check-input"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              testId="member-set-create-filtered__private"
            />

            <QESButton qesType="primary" className="mt-3" onClick={handleSubmit} qesTestId="created-filtered-member-set__submit" disabled={!source || !name}>Submit</QESButton>
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

export default MemberSetCreateFiltered;
